import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingBar from './LoadingBar';
import { SKIP_QA_SIGN_ON, ENVIRONMENT } from '../../config';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
}

const ProtectedRoute = ({ children, requireAuth = true }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // If auth is required, user is not authenticated, not loading, and not in QA mode
    // Redirect directly to Auth0 Universal Login
    if (requireAuth && !isAuthenticated && !isLoading && !SKIP_QA_SIGN_ON) {
      const returnTo = location.pathname + location.search;
      login(returnTo);
    }
  }, [requireAuth, isAuthenticated, isLoading, location, login]);

  // Show loading while checking auth status
  if (isLoading) {
    return <LoadingBar />;
  }

  // If auth is required and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // In QA mode, we should already have a user, if not something is wrong
    if (SKIP_QA_SIGN_ON) {
      // Additional security check - this should never happen in production
      if (ENVIRONMENT === 'production') {
        console.error('SECURITY VIOLATION: QA mode detected in ProtectedRoute during production');
        throw new Error('QA mode is not allowed in production environment');
      }
      return <div>Error: QA user not loaded</div>;
    }
    // Auth0 redirect is handled by useEffect
    return <LoadingBar />;
  }

  // If auth is not required but user is authenticated, allow access
  // (for pages like login that should redirect authenticated users)
  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
